// types for the aggregate (sodat) service (*only* backend)
// see https://gitlab.com/prisma-capacity/platform/sodat-service/-/blob/master/src/main/resources/open-api.yml?ref_type=heads
import { Links, OptionalLinks, SortDirection } from 'src/apis/api-utilities';
import { MonetaryValue } from 'src/apis/capacity-management/types';
import { DateInterval } from 'src/apis/monolith/types';
import { PhoneNumber } from 'src/apis/organisation-registration/types';
import { FormOption } from 'src/components/form/form-option';

export type AggregateEuRootResponse = {
  _links?: Links<'self' | 'getFinancialSecurityOptions'> &
    OptionalLinks<
      | 'requestOrganisationSubscription'
      | 'manageOrganisationSubscription'
      // only there if requestOrganisationSubscription is there as well
      | 'uploadDeclarationOfHonour'
      | 'getFinancialSecurityOptions'
      | 'getLocationOptions'
      | 'settings'
      | 'demands'
      | 'aggregatedDemands'
      | 'infoSheets'
      | 'tenders'
      | 'buyerMatches'
      | 'consortiums'
      | 'matches'
      | 'sellerMatches'
      | 'consortia'
      | 'contractReports'
      | 'getAllProcesses'
    >;
  organisationSubscriptionDetails?: {
    state: OrganisationSubscriptionStatus;
    subscriptionId: string;
    organisationTypes: OrganisationType[];
  };
  lngProcurementProcess?: ProcurementProcess;
  nbpProcurementProcess?: ProcurementProcess;
};

export type ReadQuantity = {
  startOfMonth: string;
  amount: number | null;
  lngPreferredTerminals?: LngTerminal[] | null;
};

export type Quantity = {
  startOfMonth: string;
  amount: number | null;
  lngPreferredTerminalIds?: string[] | null;
};

export type Location = {
  id: string;
  displayName: string;
  tooltip?: string;
  terminals?: LngTerminal[];
};

export type LngTerminal = {
  id: string;
  country: string;
  displayName: string;
  activeForDemandCollection?: boolean;
};

export type ProcurementProcess = {
  _links?: // <'self'> &
  // these links are conditional available based on the current phase of the process
  OptionalLinks<
    | 'myDemands'
    | 'myInfoSheets'
    | 'myTenders'
    | 'myBuyerMatches'
    | 'mySellerMatches'
    // check what happens with these:
    | 'submitDemand'
    | 'getAggregatedDemands'
    | 'mySellerMatches'
  >;
  displayProcurementId: string;
  processType: ProcessType;
  procurementProcessId: string;
  demandCollectionWindow: { start: string; end: string };
  informationSheetFinalizationWindow?: { start: string; end: string };
  tenderingWindow?: { start: string; end: string };
  matchingPublication?: string;
  earliestDemandStart: string;
  demandMonths: DemandMonth[];
  latestDemandStart: string;
  minimalMonthlyAmount: number;
  minimumOfferValidThrough?: string;
};

export type DemandMonth = {
  startOfMonth: string;
  year: number;
  month: number;
};

// eslint-disable-next-line no-restricted-syntax
export enum ProcessType {
  LNG = 'LNG_PROCUREMENT',
  NBP = 'NBP_PROCUREMENT',
}

export type GasDemandsParams = {
  limit?: number;
  offset?: number;
  processType: ProcessType[];
  currentProcessOnly: boolean;
};

export type CentralBuyerListParams = {
  limit?: number;
  offset?: number;
};

export type WriteCentralBuyerList = {
  name: string;
  members: {
    organisationName: string;
    organisationEic?: string;
    organisationVat: string;
  }[];
};

export type MyDemandsResponse = {
  _links?: Links<'self' | 'instance'> &
    OptionalLinks<'submitLngDemand' | 'submitNbpDemand'>;
  total: number;
  offset: number;
  limit: number;
  _embedded: {
    items: MyDemand[];
  };
};

export type MyDemand = {
  _links?: Links<'self'> & OptionalLinks<'delete' | 'update'>;
  procurementProcessId: string;
  demandAtLocationId: string;
  processType: ProcessType;
  consortiumId?: string;
  consortiumName?: string;
  location: Location;
  quantities: ReadQuantity[];
};

export type LNGMyDemandsResponse = {
  _links?: Links<'self'>;
  _embedded: { items: MyDemand[] };
};

export type NBPMyDemandsResponse = {
  _links?: Links<'self'>;
  _embedded: { items: MyDemand[] };
};

export type WriteLNGDemand = {
  locationId: string;
  quantities: Quantity[];
};

export type LNGProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type NBPProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type WriteNBPDemand = {
  locationId: string;
  quantities: Quantity[];
};

export type ProcurementProcessAggregatedDemandsResponse = {
  _links?: Links<'self'>;
  _embedded?: {
    items: MyDemand[];
  };
};

export type WriteGasDemand = {
  locationId: string;
  consortiumId?: string | null;
  quantities: Quantity[];
};

export type UpdateGasDemand = {
  quantities: Quantity[];
};

// quantity unit is always "GWH"
export type Amount = { value: number; quantityUnit: string };

// eslint-disable-next-line no-restricted-syntax
export enum OrganisationSubscriptionStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  REVOKED = 'REVOKED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export const organisationTypeValues = [
  'BUYER',
  'SELLER',
  'AGENT_ON_BEHALF',
] as const;
export type OrganisationType = (typeof organisationTypeValues)[number];

export type FinancialSecurity = {
  id: string;
  displayName: string;
  groupLabel?: string;
  groupDisplayName?: string;
};

export type OrganisationSubscriptionResponse = {
  _links?: Links<'self'>;
  organisationId: string;
  subscriptionId: string;
  status: OrganisationSubscriptionStatus;
  organisationTypes: OrganisationType[];
  financialSecurities: FinancialSecurity[];
  contactFirstName?: string;
  contactLastName?: string;
  contactEmail?: string;
  contactPhoneNumber?: PhoneNumber;
};

export type WriteOrganisationSubscription = {
  organisationTypes: OrganisationType[];
  preferredLocations: string[];
  // undefined for sellers
  financialSecurities?: string[];
  // just the file uuid
  declarationOfHonourFileId: string;
};

export type OrganisationSubscriptionUpdateWrite = Pick<
  OrganisationSubscriptionResponse,
  'contactFirstName' | 'contactLastName' | 'contactEmail' | 'contactPhoneNumber'
>;

export type LocationsResponse = {
  lngLocations: Location[];
  nbpLocations: Location[];
};

export type InformationSheetsParams = {
  limit?: number;
  offset?: number;
  processType?: ProcessType[];
  locationId?: string[];
  startOfMonthStart?: string;
  startOfMonthEnd?: string;
};

export type InformationSheetsResponse = {
  _links?: Links<'self' | 'first'> & OptionalLinks<'next' | 'prev'>;
  _embedded: {
    items: InformationSheetDetails[];
  };
  total: number;
  limit: number;
  offset: number;
};

export type InformationSheetDetails = {
  _links?: OptionalLinks<'decline'>;
  id: string;
  state: 'DEFAULT' | 'DECLINED';
  processType: ProcessType;
  location: Location;
  startOfMonth: string;
  maxTotalAmount: number;
  buyerAmount: number;
  consortium?: ConsortiumDisplay;
  lngPreferredTerminals?: LngTerminal[] | null;
};

export type FinancialSecurityOptionsResponse = {
  _links?: Links<'self'>;
  _embedded: {
    items: FinancialSecurity[];
  };
};

export type TenderParams = {
  limit?: number;
  offset?: number;
  processType?: ProcessType[];
};

export type TendersResponse = {
  _links?: Links<'self' | 'first' | 'instance'> &
    OptionalLinks<'next' | 'prev' | 'download'>;
  _embedded: {
    items: TenderDetails[];
  };
  total: number;
  limit: number;
  offset: number;
};

export type TenderDetails = {
  _links?: Links<'self'> &
    OptionalLinks<
      | 'deleteOffers'
      | 'editOffersV3' // use TTF component also for LNG (interface doesn't change)
      | 'submitOffersV3' // use TTF component also for LNG (interface doesn't change)
    >;
  id: string;
  displayTenderId: string;
  processType: ProcessType;
  location: Location;
  startOfMonth: string;
  totalAmount: number;
  offers: TenderOffer[];
};

// OfferJson in OpenAPI
export type TenderOffer = {
  id: string;
  offerAtTenderId: string;
  quantity: number;
  price: MonetaryValue;
  validThrough?: string;
  preferredTerminals?: LngTerminal[]; // only for LNG
  priceIndex: PriceIndex;
};

// SubmitOffersAtTenderJson in OpenAPI
export type WriteOffers = {
  offers: WriteOffer[];
};

// SubmitOfferJson in OpenAPI
export type WriteOffer = {
  quantity: number;
  price: {
    value: number;
    currency: string;
  };
  validThrough?: string | null;
  lngPreferredTerminalIds?: string[]; // may be present for lng
};

export type GetBuyerMatchesSortOrder =
  | 'DISPLAY_PROCESS_ID'
  | 'LOCATION_NAME'
  | 'START_OF_MONTH'
  | 'MATCHED_CAPACITY_AMOUNT'
  | 'DEMANDED_CAPACITY_AMOUNT'
  | 'AVERAGE_PRICE_AMOUNT';

export type GetBuyerMatchesParams = {
  processType?: ProcessType[];
  locationId?: string[];
  startOfMonthStart?: string | null;
  startOfMonthEnd?: string | null;
  consortiumId?: string[];
  processId?: string[];
  tenderDisplayId?: string | null;
  limit?: number;
  offset?: number;
  sortDirection?: SortDirection;
  sortBy?: GetBuyerMatchesSortOrder;
};

export type ConsortiumDisplay = {
  id: string;
  displayName: string;
};

export type ConsortiumSuggestion = FormOption<string>;

// if not available (BE not fully released) you can treat this as 'TTF' for NBP and 'NONE' for LNG
// `null` means the PriceIndex could not be found in the BE.
// in that case we *always* treat it as 'none' for LNG and 'TTF' for NBP.
// (the same logic applies when the BE change isn't fully rolled out, yet and price index is undefined.)
export type PriceIndex = 'none' | 'TTF' | null;

export type BuyerMatch = {
  buyerMatchId: string;
  demandId: string;
  tenderId: string;
  procurementProcessId: string;
  processType: ProcessType;
  demandDisplayId: string;
  tenderDisplayId: string;
  processDisplayId: string;
  consortium?: ConsortiumDisplay;
  location: Location;
  startOfMonth: string;
  demandedCapacity: number;
  matchedCapacity: number;
  averagePrice: number;
  priceIndex: PriceIndex;
};

export type BuyerMatches = {
  _links: Links<'self'> & OptionalLinks<'first' | 'instance' | 'download'>;
  _embedded: { items: BuyerMatch[] };
  total: number;
  limit: number;
  offset: number;
};

// BuyerMatchDetailSellerJson in open API
export type BuyerMatchDetailSeller = {
  organisation: OrganisationContactData;
  organisationContactPerson?: OrganisationContactPersonData;
  matchedCapacity: number;
  price: MonetaryValue;
  priceIndex: PriceIndex;
  validThrough?: string;
  preferredTerminals: LngTerminal[]; // only for LNG
};

// BuyerMatchDetailJson in open API
export type BuyerMatchDetail = BuyerMatch & {
  _links: Links<'self'> & OptionalLinks<'download'>;
  totalDemandedCapacity: number;
  possibleConsortiaPartners?: PossibleConsortiumPartnerData[];
  sellers: BuyerMatchDetailSeller[];
  preferredTerminals: LngTerminal[];
};

// PossibleConsortiumPartnerDataJson in open API
export type PossibleConsortiumPartnerData = OrganisationContactData & {
  preferredTerminals: LngTerminal[];
  organisationContactPerson?: OrganisationContactPersonData;
};

export type OrganisationContactData = {
  name: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    postcode: string;
    city: string;
    country?: string;
  };
  email?: string;
  phone?: string;
};

export type SellerMatchesResponse = {
  _embedded: { items: SellerMatch[] };
  _links: Links<'self'> & OptionalLinks<'first' | 'instance' | 'download'>;
  total: number;
  limit: number;
  offset: number;
};

export type SellerMatch = {
  _links?: Links<'self'>;
  procurementProcessId: string;
  processType: ProcessType;
  processDisplayId: string;
  location: Location;
  startOfMonth: string;
  totalOfferedCapacity: number;
  totalMatchedCapacity: number;
  sellerMatchId: string;
  tenderId: string;
  tenderDisplayId: string;
};

export type GetMySellerMatchesParams = {
  processType?: ProcessType[];
  locationId?: string[];
  startOfMonthStart?: string | null;
  startOfMonthEnd?: string | null;
  tenderDisplayId?: string | null;
  processId?: string[];
  sortDirection?: SortDirection;
  sortBy?: GetSellerMatchesSortOrder;
  limit?: number;
  offset?: number;
};

export type GetSellerMatchesSortOrder =
  | 'DISPLAY_PROCESS_ID'
  | 'LOCATION_NAME'
  | 'START_OF_MONTH'
  | 'MATCHED_CAPACITY_AMOUNT';

export type ConsortiaResponse = {
  _links?: Links<'self'> & OptionalLinks<'first' | 'instance' | 'create'>;
  total: number;
  limit: number;
  offset: number;
  _embedded: {
    items: Consortium[];
  };
};

export type Consortium = {
  _links: Links<'self'> & OptionalLinks<'update'>;
  id: string;
  name: string;
  members: {
    organisationName: string;
    organisationEic?: string;
    organisationVat: string;
  }[];
};

// SellerMatchDetailJson in Open API
export type SellerMatchDetail = SellerMatch & {
  totalDemandedCapacity: number;
  offers: TenderOffer[];
  sellerLngPreferredTerminals: LngTerminal[];
  buyers: SellerMatchBuyer[];
};

// SellerMatchedBuyerJson in Open API
export type SellerMatchBuyer = {
  consortiumId?: string;
  consortiumName?: string;
  organisation: OrganisationContactData;
  organisationContactPerson?: OrganisationContactPersonData;
  financialSecurities: FinancialSecurity[];
  demands: SellerMatchedDemand[];
  lngPreferredTerminals: LngTerminal[];
};

export type SellerMatchedDemand = {
  matchedCapacity: number;
  price: MonetaryValue;
  priceIndex: PriceIndex;
};

// eslint-disable-next-line no-restricted-syntax
export enum ContractState {
  CONTRACTED = 'CONTRACTED',
  IN_NEGOTIATION = 'IN_NEGOTIATION',
  NEGOTIATION_UNSUCCESSFUL = 'NEGOTIATION_UNSUCCESSFUL',
  WITHDRAWN = 'WITHDRAWN',
}

export type ContractReport = {
  _links: Links<'self' | 'update'>;
  contractReportId: string;
  displayContractReportId: string;
  contractState: ContractState;
  consortiumId?: string;
  buyerName: string;
  sellerId: string;
  sellerName: string;
  runtime?: {
    start: string;
    end: string;
  };
  amount?: number;
  location?: Location;
  lngPreferredTerminals?: LngTerminal[];
  satisfaction?: number;
  remarks?: string;
};

export type ContractReportsParams = {
  offset?: number;
  limit?: number;
  locationId?: string[] | null;
  runtimeStart?: string | null;
  runtimeEnd?: string | null;
  quantityMin?: number | null;
  quantityMax?: number | null;
  sellerId?: string[] | null;
  consortiumId?: string[] | null;
  // should only be there if 'on my behalf' is selected
  showOwnContracts?: boolean | null;
  contractId?: string | null;
  contractState?: string[] | null;
};

export type ContractReportsResponse = {
  _links?: Links<
    | 'self'
    | 'possibleSellersForCreate'
    | 'allPossibleSellers'
    | 'allPossibleConsortia'
    | 'instance'
    | 'create'
  >;
  total: number;
  offset: number;
  limit: number;
  _embedded: {
    items: ContractReport[];
  };
};

export type ContractSeller = {
  id: string;
  name: string;
};

export type ContractsSellerResponse = {
  _links?: Links<'self' | 'instance'>;
  total: number;
  offset: number;
  limit: number;
  _embedded: {
    items: ContractSeller[];
  };
};

export type PossibleSellersOrBuyersResponse = {
  items: Item[];
};

export type Item = {
  id: string;
  name: string;
};

export const contractStatusValues = [
  'CONTRACTED',
  'IN_NEGOTIATION',
  'NEGOTIATION_UNSUCCESSFUL',
  'WITHDRAWN',
] as const;

export type CreateContractReport = {
  consortiumId?: string;
  sellerOrganisationId: string;
  quantity?: number;
  contractRuntime?: DateInterval;
  locationId?: string;
  lngPreferredTerminalIds?: string[];
  contractStatus: (typeof contractStatusValues)[number];
  satisfaction?: number;
  remarks?: string;
};

export type UpdateContractReport = {
  quantity?: number;
  contractRuntime?: DateInterval;
  locationId?: string;
  lngPreferredTerminalIds?: string[];
  contractStatus: (typeof contractStatusValues)[number];
  satisfaction?: number;
  remarks?: string;
};

export type OrganisationContactPersonData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string[];
};

export type ProcessSummary = {
  id: string;
  label: string;
  type: ProcessType;
};

export type GetAllProcessesResponse = {
  total: number;
  offset: number;
  limit: number;
  _embedded: {
    items: ProcessSummary[];
  };
};
